import { CommonModule, CurrencyPipe, NgClass } from '@angular/common';
import { AfterContentChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { OrdersActionType } from '@app/orders/models/orders.model';
import { OrdersService } from '@app/orders/services/orders.service';
import {
  FinancialTransactionRequestDto,
  FinancialTransactionResponseDto,
  OrderItemByPaymentIdResponseDto,
  PayerResponseDto,
  PaymentModeResponseDto,
  PaymentOperationTypeEnumRequestDto,
  PaymentTypeEnumRequestDto,
  SalesUserResponseDto,
  UpdatePaymentRequestDto,
} from '@core/services/apiclient';
import { select, Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromOrders from '@root-store/orders-store/index';
import { SalesPaymentModeModel } from '@shared/models/sales-payment-mode.model';
import { SalesService } from '@sales/services/sales.service';
import { DialogComponent } from '@shared/components';
import {
  AbstractPaymentModeFormComponent,
  AbstractPaymentModeFormUtils,
} from '@shared/components/functional/abstract-payment-mode-form/abstract-payment-mode-form.component';
import { CustomerSearchItem } from '@shared/customer/models/customer-search-item.model';
import { CustomerSearchStateService } from '@shared/customer/services/customer-search-state.service';
import { CustomerService } from '@shared/customer/services/customer.service';
import { MomentPipe } from '@shared/pipes/moment.date.format';
import { SharedModule } from '@shared/shared.module';
import { combineLatest, Observable, of, Subject, takeUntil } from 'rxjs';
import { clone } from 'underscore';

@Component({
  selector: 'app-orders-payments-edit-dialog',
  templateUrl: './orders-payments-edit-dialog.component.html',
  styleUrls: ['./orders-payments-edit-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CustomerSearchStateService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatButtonModule,
    MatDialogModule,
    NgClass,
    SharedModule,
  ],
})
export class OrdersPaymentsEditDialogComponent
  extends AbstractPaymentModeFormComponent
  implements OnInit, AfterContentChecked
{
  @ViewChild(DialogComponent) dialog: DialogComponent;
  @ViewChild('blurryBloc', { read: ElementRef }) blurryBlocContainer: ElementRef;
  private readonly store = inject(Store<fromOrders.State>);
  private readonly customerService = inject(CustomerService);
  private readonly payerSearch = inject(CustomerSearchStateService);
  private readonly salesService = inject(SalesService);
  private readonly ordersService = inject(OrdersService);
  private readonly datePipe = inject(MomentPipe);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  isTopBlurryEffectVisible = false;
  public paymentSummary: OrderItemByPaymentIdResponseDto[];
  public editType: OrdersActionType;
  public isPaymentEditType: boolean;
  public ordersActionType = OrdersActionType;
  public operatorUser: CustomerSearchItem;
  public payerUser: CustomerSearchItem;
  public currentPaymentItems: SalesPaymentModeModel[];
  public paymentTypes = [PaymentTypeEnumRequestDto.Direct];
  public paymentModes: PaymentModeResponseDto[];
  private initialDialogState: string;
  private editedTotalAmount = 0;
  private paymentId: number;
  public amountToReturn: number;

  public operatorData: CustomerSearchItem[] = [];
  public operatorSelectedId = 0;
  public payerSelectedId = 0;
  public isOperatorLoading = false;

  get payerData(): CustomerSearchItem[] {
    return this.payerSearch.customerData;
  }
  get isPayerLoading(): boolean {
    return this.payerSearch.isLoading;
  }

  private paymentDialogClosed$ = new Subject<void>();

  ngOnInit(): void {
    this.paymentModes$ = this.getPaymentModes();
    this.initOperatorList();
    this.store
      .pipe(select(fromOrders.selectors.selectOrderActionsPayload), takeUntil(this.destroy$))
      .subscribe({
        next: (payload) => {
          this.currentPaymentItems = [];
          if (payload) {
            this.isPaymentEditType = this.isPaymentEditAction(payload?.orderActionType);
            if (this.isPaymentEditType) {
              this.editType = payload?.orderActionType;
              this.paymentId = payload?.options?.paymentId;
              this.operatorUser = this.getOperatorAsCustomer(payload?.options?.operator);
              this.payerUser = this.getPayerAsCustomer(payload?.options?.payer);
              this.operatorSelectedId = this.operatorUser?.id ?? 0;
              this.payerSelectedId = this.payerUser?.id ?? 0;
              this.payerSearch.clearData();
              this.payerSearch.addToListIfMissing(this.payerUser);
              this.getPayments();
              this.dialog.open();
            }
          } else {
            if (this.dialog) {
              this.dialog.close();
              this.paymentDialogClosed$.next();
            }
          }
          this.cdr.markForCheck();
        },
      });
  }

  ngAfterContentChecked(): void {
    this.applyBlurryEffect();
  }

  protected getPaymentModes(): Observable<PaymentModeResponseDto[]> {
    return this.salesService.getPaymentMode();
  }

  protected getTotalAmount(): Observable<number> {
    return of(this.computeEditedTotalAmount());
  }

  private computeEditedTotalAmount(): number {
    return this.moneyService.calculateTotalAmount(this.currentPaymentItems || [], (item) =>
      Math.abs(item?.amount || 0),
    );
  }

  protected createForm(): void {
    this.editedTotalAmount = this.computeEditedTotalAmount();
    this.form = this.formBuilder.group(
      {
        rows: this.formBuilder.array(this.getInitialPaymentRows()),
        // add these two controls to compare initial state to current state
        payer: this.formBuilder.control(this.payerUser?.id),
        operator: this.formBuilder.control(this.operatorUser?.id),
      },
      {
        validators: [
          this.hasUnfilledAmountValidator(),
          // The edited transaction keeps its total: a split over three modes or more could otherwise
          // leave it incomplete, the one and two row cases being held by applyTotalIfSingleRow and
          // applyPaymentBalance.
          this.fixedTotalAmountValidator(() => this.editedTotalAmount),
        ],
        asyncValidators: [this.paymentModeAmountsSumValidator(this.totalAmount$)],
      },
    );
    this.initialDialogState = JSON.stringify(this.form.value);
  }

  public getPayments() {
    const paymentItems$ = this.ordersService.getPaymentDetails(this.paymentId);
    combineLatest([paymentItems$, this.paymentModes$])
      .pipe(takeUntil(this.paymentDialogClosed$))
      .subscribe({
        next: ([payments, paymentModes]) => {
          this.paymentModes = paymentModes;
          this.paymentSummary = payments?.orderItems;
          this.currentPaymentItems = this.mapOrderPaymentsToPaymentRows(payments?.items);
          this.totalAmount$ = this.getTotalAmount();
          this.createForm();
          this.cdr.markForCheck();
        },
      });
  }

  public closePaymentEdit() {
    this.store.dispatch(fromOrders.actions.resetOrderAction());
  }

  public submitPaymentEdit() {
    this.store.dispatch(
      fromOrders.actions.processOrderPaymentEdit({
        paymentEditPayload: this.buildPaymentEditPayload(),
        paymentType: this.mapActionToOperation(),
      }),
    );
  }

  private buildPaymentEditPayload(): UpdatePaymentRequestDto {
    return {
      paymentId: this.paymentId,
      paymentItems: this.getPaymentItems(this.form?.controls?.rows?.value),
      operatorUserId: this.operatorUser?.id,
    };
  }

  private getPaymentItems(
    rows: { amount: number; mode: PaymentModeResponseDto }[],
  ): FinancialTransactionRequestDto[] {
    const paymentOperationType = this.mapActionToOperation();
    // Every row carries a required payment mode and the validate button stays disabled while one is
    // unresolved. Reading it without a guard is deliberate: an absent paymentModeId is dropped by
    // JSON.stringify and the backend would bind 0, cancelling the refund to recreate it on a mode
    // that does not exist.
    return AbstractPaymentModeFormUtils.getDirectPaymentResult(clone(rows), this.amountToReturn).map(
      ({ amount, mode }) => ({
        amount,
        paymentModeId: mode.id,
        paymentOperationType,
        payerId: this.form?.controls?.payer?.value,
        paymentId: this.paymentId,
      }),
    );
  }

  private getPaymentModeById(paymentModeId: number) {
    return (this.paymentModes || []).find(({ id }) => id === paymentModeId);
  }

  private initOperatorList() {
    this.isOperatorLoading = true;
    this.customerService
      .getAllOperators()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (operators) => {
          this.operatorData = (operators || []).map((operator) => this.getOperatorAsCustomer(operator));
          this.isOperatorLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isOperatorLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private getOperatorAsCustomer(operator: SalesUserResponseDto): CustomerSearchItem {
    return {
      id: operator?.loginId,
      name: operator?.userName,
      email: operator?.name,
      type: 'Normal',
    };
  }

  private getPayerAsCustomer(payer: PayerResponseDto): CustomerSearchItem {
    return {
      id: payer?.id,
      name: payer?.name,
      email: payer?.name,
      type: payer?.isAnonymous ? 'Anonymized' : 'Normal',
    };
  }

  private mapOrderPaymentsToPaymentRows(payments: FinancialTransactionResponseDto[]) {
    return (payments || []).map((paymentItem: FinancialTransactionResponseDto) => {
      const paymentMode = this.getPaymentModeById(paymentItem?.paymentModeId);
      return {
        amount: Math.abs(paymentItem?.amount),
        paymentTypeLabel: paymentMode?.name,
        paymentTypeId: paymentMode?.id,
        paymentMode,
      } as SalesPaymentModeModel;
    });
  }

  public onOperatorChange(user: CustomerSearchItem) {
    this.operatorUser = user;
    this.operatorSelectedId = user?.id ?? 0;
    this.form.controls?.operator?.patchValue(this.operatorUser?.id);
  }

  public onCustomerChange(user: CustomerSearchItem) {
    this.payerUser = user;
    this.payerSelectedId = user?.id ?? 0;
    this.form.controls?.payer?.patchValue(this.payerUser?.id);
  }

  public onPayerSearchTrigger(event: { searchTerm: string }) {
    this.payerSearch.triggerSearch(event.searchTerm);
  }

  public isFormInvalid() {
    return (
      !this.operatorUser ||
      !this.payerUser ||
      !this.form?.valid ||
      JSON.stringify(this.form.value) === this.initialDialogState
    );
  }

  public getSummaryLabel = (orderItem: OrderItemByPaymentIdResponseDto) => {
    return `${this.translate.instant('orders.folderNumber')} ${orderItem?.id} (${this.translate.instant(
      'orders.orderNumber',
    )} ${orderItem?.orderId}) - ${orderItem?.label} ${this.datePipe.transform(
      orderItem?.sessionDate,
      'dd/MM/yyyy - HH:mm',
    )} - ${this.currencyPipe.transform(orderItem?.amount, 'EUR', 'symbol', '0.2-2', 'fr')}`;
  };

  private mapActionToOperation = (): PaymentOperationTypeEnumRequestDto =>
    this.editType === OrdersActionType.RefundEdit
      ? PaymentOperationTypeEnumRequestDto.Refund
      : PaymentOperationTypeEnumRequestDto.Payment;

  private getInitialPaymentRows() {
    return (this.currentPaymentItems || []).map((payment) => {
      return this.formBuilder.group({
        mode: [payment?.paymentMode, Validators.required],
        amount: [payment?.amount, [Validators.required, Validators.min(0)]],
      });
    });
  }

  applyBlurryEffect() {
    this.isTopBlurryEffectVisible =
      Math.round(
        this.blurryBlocContainer?.nativeElement?.offsetHeight +
          this.blurryBlocContainer?.nativeElement?.scrollTop,
      ) < this.blurryBlocContainer?.nativeElement?.scrollHeight;
  }

  private isPaymentEditAction = (actionType: OrdersActionType) =>
    actionType === OrdersActionType.PaymentEdit || actionType === OrdersActionType.RefundEdit;
}
